import { motion } from 'framer-motion';
import { Phone, Mic, Clock, Settings, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../../../context/LangContext';
import { useCurrency } from '../../../context/CurrencyContext';

const VOICE_IMAGE = "https://customer-assets.emergentagent.com/job_d50654b2-90f4-4249-b46d-f1035aca848d/artifacts/chks1dkw_Gemini_Generated_Image_llgm3hllgm3hllgm.png";

export const VoiceAgentPricing = () => {
  const { lang } = useLang();
  const { currency, getPrice, getCurrencySymbol } = useCurrency();

  const setupPrice = getPrice('voiceSetup');
  const perMinPrice = getPrice('voicePerMin');

  const features = [
    {
      icon: CheckCircle2,
      text: lang === 'fr' 
        ? "Facturation à l'usage réel, sans engagement minimum"
        : "Pay-per-use billing, no minimum commitment"
    },
    {
      icon: Settings,
      text: lang === 'fr'
        ? "Tarif ajusté selon les intégrations CRM/ERP et le périmètre fonctionnel"
        : "Pricing adjusted based on CRM/ERP integrations and functional scope"
    },
    {
      icon: Clock,
      text: lang === 'fr'
        ? "Arrêtez quand vous voulez, sans frais cachés"
        : "Stop anytime, no hidden fees"
    },
  ];

  return (
    <section data-testid="voice-agent-pricing" className="relative py-20 bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Section Title */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-gold/30 bg-brand-gold/10 text-brand-gold text-sm font-medium mb-4">
            <Phone size={16} />
            {lang === 'fr' ? 'Agent IA Vocal' : 'Voice AI Agent'}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {lang === 'fr' ? 'Agent IA Vocal' : 'Voice AI Agent'}
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            {lang === 'fr' 
              ? "Un coût initial one-shot, puis facturation à l'usage. Vous gardez le contrôle total."
              : "One-time setup cost, then pay-per-use. You stay in complete control."
            }
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Pricing Card */}
          <motion.div
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-gold/10 via-white/[0.02] to-brand-green/10 backdrop-blur-xl border border-brand-gold/30 p-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-gold via-brand-green to-transparent" />
            
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-brand-gold/20 flex items-center justify-center">
                <Mic size={24} className="text-brand-gold" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {lang === 'fr' ? 'Tarification Vocale' : 'Voice Pricing'}
                </h3>
                <p className="text-sm text-neutral-400">
                  {lang === 'fr' ? 'Setup + Usage' : 'Setup + Usage'}
                </p>
              </div>
            </div>

            {/* Setup Price */}
            <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <p className="text-sm text-neutral-400 mb-2">
                {lang === 'fr' ? 'Setup initial (one-shot)' : 'Initial setup (one-time)'}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-neutral-500">{lang === 'fr' ? 'à partir de' : 'from'}</span>
                <span className="text-3xl font-bold text-white">{setupPrice.toLocaleString()}</span>
                <span className="text-base text-neutral-400">{getCurrencySymbol()}</span>
              </div>
            </div>

            {/* Per Minute Price */}
            <div className="mb-8 p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <p className="text-sm text-neutral-400 mb-2">
                {lang === 'fr' ? 'Usage voix' : 'Voice usage'}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-neutral-500">{lang === 'fr' ? 'à partir de' : 'from'}</span>
                <span className="text-3xl font-bold text-white">{perMinPrice.toFixed(2)}</span>
                <span className="text-base text-neutral-400">{getCurrencySymbol()} / min</span>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <feature.icon size={18} className="text-brand-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-neutral-300">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              data-testid="voice-cta"
              className="w-full text-center py-4 rounded-xl text-sm font-semibold transition-all duration-300 bg-gradient-to-r from-brand-gold to-brand-green text-black hover:shadow-[0_0_30px_rgba(224,197,104,0.4)] block"
            >
              {lang === 'fr' ? 'Demander un devis' : 'Request a quote'}
            </Link>
          </motion.div>

          {/* Voice Agent Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-gold/10 border border-white/[0.05]">
              <img 
                src={VOICE_IMAGE} 
                alt={lang === 'fr' ? "Agent IA Vocal - Interface de communication vocale" : "Voice AI Agent - Voice communication interface"}
                className="w-full h-auto"
                data-testid="voice-agent-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Floating badge */}
            <motion.div 
              className="absolute -bottom-4 -right-4 bg-[#0A0A0F] border border-brand-gold/30 rounded-xl px-4 py-3 shadow-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <div className="text-xs text-neutral-500 mb-1">
                {lang === 'fr' ? 'Disponibilité' : 'Availability'}
              </div>
              <div className="text-xl font-bold text-brand-gold">24/7</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
